// Собственное меню OMA (как у каждого сервиса Orbis): разделы, счётчик «Ждёт тебя», внизу — связь и версия.
import { useAuth } from '../auth.ts'
import { isTaskPath, Link, useLocation } from '../router.tsx'
import { useLive } from '../store.ts'
import { monogram } from './AgentAvatar.tsx'
import { Icon, type IconName } from './Icon.tsx'
import { AgentClock, LiveSummary, LogoutButton, SafetyBadge, ThemeButton } from './Topbar.tsx'
import { cx } from './ui.tsx'

interface NavItem {
  to: string
  label: string
  icon: IconName
  match: (p: string) => boolean
  count?: number
  /** Второй счётчик: готовые задачи, которые ещё не смотрели. */
  done?: number
}

export function useEffectivePath(): string {
  const loc = useLocation()
  if (isTaskPath(loc.pathname)) return (loc.state?.bg ?? '/').split('?')[0] ?? '/'
  return loc.pathname
}

export function Sidebar({ onNavigate }: { onNavigate?: () => void }) {
  const path = useEffectivePath()
  const open = useLive((s) => s.stats?.open_escalations ?? 0)
  const done = useLive((s) => s.stats?.done_unseen ?? 0)
  const connected = useLive((s) => s.connected)
  const { user } = useAuth()
  const name = user?.name || user?.email || ''

  const nav: NavItem[] = [
    { to: '/', label: 'Доска', icon: 'kanban', match: (p) => p === '/' },
    { to: '/inbox', label: 'Ждёт тебя', icon: 'inbox', match: (p) => p.startsWith('/inbox'), count: open, done },
    { to: '/agents', label: 'Агенты', icon: 'bot', match: (p) => p.startsWith('/agents') },
    { to: '/machines', label: 'Машины', icon: 'server', match: (p) => p.startsWith('/machines') },
    { to: '/chats', label: 'Чаты', icon: 'message', match: (p) => p.startsWith('/chats') },
    { to: '/map', label: 'Карта', icon: 'graph', match: (p) => p.startsWith('/map') },
    { to: '/feed', label: 'Эфир', icon: 'radio', match: (p) => p.startsWith('/feed') },
    { to: '/knowledge', label: 'Знания', icon: 'book', match: (p) => p.startsWith('/knowledge') },
    { to: '/settings', label: 'Настройки', icon: 'settings', match: (p) => p.startsWith('/settings') },
  ]

  return (
    <aside className="side" aria-label="Меню OMA">
      <nav className="nav" aria-label="Разделы OMA">
        {nav.map((n) => {
          const active = n.match(path)
          return (
            <Link key={n.to} to={n.to} className="ni" aria-current={active ? 'page' : undefined} onClick={onNavigate}>
              <Icon name={n.icon} size={18} strokeWidth={1.7} />
              <span className="ni-lbl">{n.label}</span>
              {n.count ? (
                <span className="cnt warn" title="Вопросы агентов" aria-label={`${n.count} открытых вопросов`}>
                  {n.count}
                </span>
              ) : null}
              {n.done ? (
                <span className={cx('cnt ok', n.count ? '-ml-1' : '')} title="Готовые задачи, ещё не просмотрены" aria-label={`${n.done} готовых задач не просмотрено`}>
                  {n.done}
                </span>
              ) : null}
            </Link>
          )
        })}
      </nav>

      {/* на узких экранах статус из top-bar переезжает сюда */}
      <div className="mx-3 mt-3 flex flex-col gap-2 rounded-card border border-line bg-surface px-3 py-2.5 min-[1440px]:hidden">
        <div className="lbl">Сейчас</div>
        <AgentClock className="inline-flex min-[380px]:hidden" />
        <LiveSummary className="block leading-relaxed" wrap />
        <SafetyBadge className="self-start" />
      </div>

      {/* на телефоне профиль, тема и выход не помещаются в top-bar — они здесь */}
      {user ? (
        <div className="mx-3 mt-3 flex min-w-0 items-center gap-2 rounded-card border border-line bg-surface py-1.5 pr-1 pl-2 min-[640px]:hidden">
          <span className="av" aria-hidden="true">
            {monogram(name)}
          </span>
          <span className="min-w-0 flex-1 leading-tight" title={user.email}>
            <span className="block truncate text-[12.5px] font-semibold">{name}</span>
            <span className="block truncate text-[10.5px] text-muted">{user.role === 'admin' ? 'владелец' : user.role}</span>
          </span>
          <ThemeButton />
          <LogoutButton />
        </div>
      ) : null}

      <div className="sidefoot" role="status" aria-live="polite">
        <span
          aria-hidden="true"
          className={cx('inline-block h-[7px] w-[7px] shrink-0 rounded-full', connected ? 'bg-ok' : 'bg-danger')}
        />
        <span>{connected ? 'live' : 'нет связи'}</span>
        <span className="ml-auto">OMA {__OMA_VERSION__}</span>
      </div>
    </aside>
  )
}
